import * as React from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import AuthService from "../../services/auth.service";
import EventBus from "../../common/EventBus";
import new_logo from "../../logo_new.svg";
import MobileMenu from "./MobileMenu";

// Icons
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import MapIcon from "@mui/icons-material/Map";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

export default function ButtonAppBar({ toggleColorMode }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentUser, setCurrentUser] = React.useState(
    AuthService.getCurrentUser(),
  );

  const [anchorElUser, setAnchorElUser] = React.useState(null);

  React.useEffect(() => {
    const user = AuthService.getCurrentUser();

    if (user) {
      setCurrentUser(user);
    }

    EventBus.on("logout", () => {
      logOut();
    });

    return () => {
      EventBus.remove("logout");
    };
  }, []);

  const logOut = () => {
    AuthService.logout();
    setCurrentUser(undefined);
    window.location.reload();
  };

  const currentPath = location.pathname;
  const isActive = path => currentPath === path;

  const handleOpenUserMenu = event => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const linkStyle = path => ({
    mx: 0.5,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: isActive(path) ? 600 : 500,
    color: isActive(path) ? "primary.main" : "text.primary",
    bgcolor: isActive(path)
      ? theme.palette.mode === "dark"
        ? "rgba(144, 202, 249, 0.16)"
        : "rgba(25, 118, 210, 0.08)"
      : "transparent",
    "&:hover": {
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.04)",
    },
  });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        sx={{
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(30, 41, 59, 0.85)"
              : "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.05)",
          color: "text.primary",
          boxShadow: "none",
        }}>
        <Toolbar sx={{ minHeight: "64px" }}>
          <Typography display="none">MED.LOG&DEL</Typography>
          <IconButton
            sx={{ mr: 2 }}
            onClick={() => {
              currentUser?.role === 6
                ? navigate("/agent")
                : currentUser?.role === 3
                  ? navigate("/courier")
                  : currentUser?.role === 7
                    ? navigate("/doctor")
                    : currentUser?.role === 8
                      ? navigate("/doctor_admin")
                      : navigate("/home");
            }}>
            <img src={new_logo} width={40} height={40} alt="Logo" />
          </IconButton>

          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }}></Box>
              <MobileMenu
                currentUser={currentUser}
                navigate={navigate}
                logOut={logOut}
                theme={theme}
                toggleColorMode={toggleColorMode}
              />
            </>
          ) : (
            <>
              <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                {currentUser?.role === 3 && (
                  <>
                    <Button
                      sx={linkStyle("/today")}
                      startIcon={<CalendarTodayIcon />}
                      onClick={() => navigate("/today")}>
                      Сегодня
                    </Button>
                    <Button
                      sx={linkStyle("/tomorrow")}
                      startIcon={<EventIcon />}
                      onClick={() => navigate("/tomorrow")}>
                      Завтра
                    </Button>
                  </>
                )}

                {currentUser?.role === 7 && (
                  <>
                    <Button
                      sx={linkStyle("/add_pacient")}
                      startIcon={<PersonAddIcon />}
                      onClick={() => navigate("/add_pacient")}>
                      Пациент
                    </Button>
                    <Button
                      sx={linkStyle("/store_pacients")}
                      startIcon={<PeopleIcon />}
                      onClick={() => navigate("/store_pacients")}>
                      История
                    </Button>
                  </>
                )}

                {currentUser?.role === 6 && (
                  <Button
                    sx={linkStyle("/agentpanel")}
                    startIcon={<DashboardIcon />}
                    onClick={() => navigate("/agentpanel")}>
                    Панель Агента
                  </Button>
                )}

                {currentUser?.role === 4 && (
                  <Button
                    sx={linkStyle("/mod")}
                    startIcon={<SecurityIcon />}
                    onClick={() => navigate("/mod")}>
                    Модератор
                  </Button>
                )}

                {currentUser?.role === 8 && (
                  <>
                    <Button
                      sx={linkStyle("/lpu")}
                      startIcon={<LocalHospitalIcon />}
                      onClick={() => navigate("/lpu")}>
                      ЛПУ
                    </Button>
                    <Button
                      sx={linkStyle("/order-map")}
                      startIcon={<MapIcon />}
                      onClick={() => navigate("/order-map")}>
                      Карта заказов
                    </Button>
                  </>
                )}

                {currentUser?.role === 1 && (
                  <Button
                    sx={linkStyle("/admin/")}
                    startIcon={<DashboardIcon />}
                    onClick={() => navigate("/admin/")}>
                    Админ
                  </Button>
                )}

                {currentUser?.role === 1 && (
                  <Button
                    sx={linkStyle("/logisticians")}
                    startIcon={<BadgeIcon />}
                    onClick={() => navigate("/logisticians")}>
                    Логисты
                  </Button>
                )}

                {currentUser?.role === 2 && (
                  <Button
                    sx={linkStyle("/home?my=1")}
                    startIcon={<AssignmentIndIcon />}
                    onClick={() => window.open("/home?my=1", "_blank")}>
                    Личные заказы
                  </Button>
                )}

                {(currentUser?.role === 1 || currentUser?.role === 9) && (
                  <Button
                    sx={linkStyle("/leads")}
                    startIcon={<ContactPhoneIcon />}
                    onClick={() => navigate("/leads")}>
                    Лиды (Юр лица)
                  </Button>
                )}

                {currentUser?.role !== 3 &&
                  currentUser?.role !== 6 &&
                  currentUser && (
                    <Button
                      sx={linkStyle("/neworder")}
                      startIcon={<AddCircleIcon />}
                      onClick={() => window.open("/neworder")}>
                      Создать заказ
                    </Button>
                  )}

                {(currentUser?.role === 1 ||
                  (currentUser?.role === 2 && currentUser?.company === 1)) && (
                  <Button
                    sx={linkStyle("/messages")}
                    startIcon={<ChatBubbleIcon />}
                    onClick={() => navigate("/messages")}>
                    Сообщения
                  </Button>
                )}
              </Box>

              <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={toggleColorMode}
                  color="inherit"
                  sx={{ mr: 1 }}>
                  {theme.palette.mode === "dark" ? (
                    <Brightness7Icon />
                  ) : (
                    <Brightness4Icon />
                  )}
                </IconButton>

                {currentUser ? (
                  <>
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          width: 36,
                          height: 36,
                          fontSize: "1rem",
                        }}>
                        {currentUser.username?.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                    <Menu
                      sx={{ mt: "45px" }}
                      id="menu-appbar"
                      anchorEl={anchorElUser}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      open={Boolean(anchorElUser)}
                      onClose={handleCloseUserMenu}>
                      <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {currentUser.username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Роль: {currentUser.role}
                        </Typography>
                      </Box>
                      <MenuItem
                        onClick={() => {
                          handleCloseUserMenu();
                          navigate("/profile");
                        }}>
                        <ListItemIcon>
                          <PersonIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography textAlign="center">Профиль</Typography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleCloseUserMenu();
                          logOut();
                        }}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <Typography textAlign="center" color="error">
                          Выйти
                        </Typography>
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    sx={{ borderRadius: "20px", textTransform: "none" }}
                    onClick={() => navigate("/login")}>
                    Войти
                  </Button>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
